import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import AssetBrowser from '@/components/AssetBrowser';
import ChatPanel from '@/components/ChatPanel';
import Collapsible from '@/components/Collapsible';
import PhaseApproval from '@/components/production/gates/PhaseApproval';
import ShotEditor from '@/components/ShotEditor';
import WorkQueue from '@/components/WorkQueue';
import ActiveCrewPanel from '@/components/production/ActiveCrewPanel';
import PhaseEta from '@/components/production/PhaseEta';
import ProductionHeader from '@/components/production/ProductionHeader';
import ProductionRail from '@/components/production/ProductionRail';
import ResizableLogPanel from '@/components/production/ResizableLogPanel';
import ShotProgressCard from '@/components/production/ShotProgressCard';
import TelemetryPanel from '@/components/production/TelemetryPanel';
import { ProductionProvider } from '@/context/ProductionContext';
import { useProductionProgress } from '@/hooks/useProduction';
import { useProductionSteps } from '@/hooks/useProductionSteps';

/**
 * The production console: watch a film get made, and steer it.
 *
 * Layout is a three-column reading order — WHERE the film is (the production-path
 * rail), WHO is working on it (crew + telemetry), and WHAT they produced (the active
 * step) — over a Director's Log, with the Director chat floating.
 *
 * This page only composes; every panel lives in `components/production/`.
 */
export default function Production() {
  const { id, projectId } = useParams<{ id: string; projectId: string }>();
  const { t } = useTranslation('production');
  const state = useProductionProgress(id);

  const [activeKey, setActiveKey] = useState('');

  // Follow the pipeline: reveal the step a new asset landed in as each stage starts
  // producing. Auto-follow disengages the moment the producer picks a step themselves,
  // so we never yank them away from something they chose to look at.
  const userPinnedStep = useRef(false);
  const selectStep = useCallback((key: string) => {
    userPinnedStep.current = true;
    setActiveKey(key);
  }, []);
  useEffect(() => {
    if (userPinnedStep.current || !state.focusTab) return;
    // Never jump to a generation-only step that a footage run doesn't render.
    if (state.productionType === 'footage' && (state.focusTab === 'design' || state.focusTab === 'storyboard')) return;
    setActiveKey(state.focusTab);
  }, [state.focusSeq, state.focusTab, state.productionType]);

  // Shots with a rework/reshoot still pending (reported by the Work Queue) — their
  // tiles get the "Rework queued" marker so they aren't flagged twice.
  const [pendingRework, setPendingRework] = useState<Set<string>>(new Set());
  // Per-asset counter bumped when a rework FINISHES (left the pending set) — used to
  // cache-bust the tile so the regenerated image actually replaces the old one.
  const [reworkBust, setReworkBust] = useState<Record<string, number>>({});
  const prevPendingRef = useRef<Set<string>>(new Set());

  const handlePendingTargets = useCallback((targets: Set<string>) => {
    const finished = [...prevPendingRef.current].filter((k) => !targets.has(k));
    if (finished.length) {
      setReworkBust((b) => {
        const nb = { ...b };
        for (const k of finished) nb[k] = (nb[k] || 0) + 1;
        return nb;
      });
    }
    prevPendingRef.current = targets;
    setPendingRework((prev) => {
      if (prev.size === targets.size && [...targets].every((t) => prev.has(t))) return prev;
      return targets;
    });
  }, []);

  // Regeneration versions from asset_ready re-fires + explicit rework busts.
  const mergedBust = useMemo(() => {
    const m: Record<string, number> = { ...reworkBust };
    for (const [k, v] of Object.entries(state.assetVersions || {})) m[k] = (m[k] || 0) + v;
    return m;
  }, [reworkBust, state.assetVersions]);

  const ctx = useMemo(
    () => ({
      productionId: id,
      assets: state.assets,
      shotPlan: state.shotPlan,
      pendingRework,
      finalAssets: state.finalAssets ?? new Set<string>(),
      running: state.status === 'running',
      reworkBust: mergedBust,
      vertical: state.aspectRatio === '9:16',
      storyboardSheet: state.storyboardFormat === 'sheet',
    }),
    [
      id,
      state.assets,
      state.shotPlan,
      state.finalAssets,
      state.status,
      state.aspectRatio,
      state.storyboardFormat,
      pendingRework,
      mergedBust,
    ],
  );

  const steps = useProductionSteps(state, id, {
    shotEditor:
      state.status === 'complete' && state.shotPlan != null && id != null ? (
        <ShotEditor
          productionId={id}
          shotPlan={state.shotPlan as ComponentProps<typeof ShotEditor>['shotPlan']}
          onReassemble={() => window.location.reload()}
        />
      ) : null,
    assetBrowser:
      id && state.availableAssets.length > 0 ? <AssetBrowser productionId={id} availableAssets={state.availableAssets} /> : null,
  });

  const active = steps.find((s) => s.key === activeKey) ?? steps[0];
  const running = state.status === 'running';

  return (
    <ProductionProvider value={ctx}>
      <div className="flex h-full flex-col bg-background">
        <ProductionHeader
          state={state}
          productionId={id}
          backPath={projectId ? `/projects/${projectId}` : '/projects'}
          backLabel={projectId ? t('rail.backToProject') : t('rail.backToProjects')}
        />

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[230px_300px_minmax(0,1fr)]">
          <div className="min-h-0 overflow-y-auto border-b border-border p-4 lg:border-b-0 lg:border-r">
            <ProductionRail steps={steps} activeKey={active?.key ?? ''} onSelect={selectStep} />
          </div>

          <div className="min-h-0 space-y-4 overflow-y-auto border-b border-border p-4 lg:border-b-0 lg:border-r">
            <Collapsible title={t('panel.crew')} defaultOpen>
              <ActiveCrewPanel
                crew={state.activeCrew}
                crewHistory={state.crewHistory}
                phase={state.currentPhase}
                telemetry={state.telemetry}
                live={running}
              />
            </Collapsible>
            <Collapsible title={t('panel.telemetry')}>
              <TelemetryPanel telemetry={state.telemetry} />
            </Collapsible>
            {state.shotProgress && (
              <Collapsible title={t('panel.shotProgress')}>
                <div className="space-y-2">
                  <PhaseEta phaseTiming={state.phaseTiming} running={running} />
                  <ShotProgressCard shotProgress={state.shotProgress} />
                </div>
              </Collapsible>
            )}
          </div>

          <div className="min-h-0 space-y-4 overflow-y-auto px-7 py-5">
            {/* The review gate and the rework queue stay pinned: a production paused
                on a gate waits up to 12 hours, so it must never be a click away. */}
            {id && <PhaseApproval productionId={id} running={running} vertical={ctx.vertical} />}
            {id && (
              <WorkQueue
                productionId={id}
                running={running}
                liveMessages={state.chat}
                onPendingTargets={handlePendingTargets}
                finalAssets={state.finalAssets}
              />
            )}
            {active?.node}
          </div>
        </div>

        <ResizableLogPanel logs={state.logs} />
      </div>

      {/* Live Director chat — continuous steering while the run is in flight. */}
      {id && <ChatPanel productionId={id} liveMessages={state.chat} />}
    </ProductionProvider>
  );
}
